import { Box, Typography } from "@mui/material";
import type { TFunction } from "i18next";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { FONT_SIZE, INK } from "@/theme/tokens";
import MetaChip from "@/ui/Badges/MetaChip";
import type { OutreachSignUp } from "@/utils/models";
import type { OutreachApprovalTone } from "./OutreachApprovalShell";
import type { ApprovalView, ApprovalViewKind } from "./outreachApprovalView";

type Props = { view: Extract<ApprovalView, { kind: ApprovalViewKind.Waiting }>; tone: OutreachApprovalTone };

/** What we checked before recommending: their role, their week with this shift, and their schedule. Warnings in amber. */
const checksOf = (t: TFunction, signUp: OutreachSignUp): { id: string; label: string; warn: boolean }[] => {
	const { role, hoursThatWeek, overtimeHours, hasConflict } = signUp.check;
	return [
		{ id: "role", label: role, warn: false },
		overtimeHours === 0
			? { id: "hours", label: t("request.outreach.approval.check.no_overtime", { hours: hoursThatWeek }), warn: false }
			: { id: "hours", label: t("request.outreach.approval.check.overtime", { overtime: overtimeHours, hours: hoursThatWeek }), warn: true },
		hasConflict
			? { id: "conflict", label: t("request.outreach.approval.check.conflict"), warn: true }
			: { id: "conflict", label: t("request.outreach.approval.check.no_conflict"), warn: false },
	];
};

/** Who accepted, then whether we recommend them and what we checked — or, on a filled shift, that there's no spot left. */
export const OutreachApprovalWaitingMessage: FC<Props> = ({ view, tone }) => {
	const { t } = useTranslation();
	const { signUp, recommended, moreWaiting, full } = view;
	return (
		<Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 0.5 }}>
			<Typography data-testid="outreach-approval-title" sx={{ fontSize: FONT_SIZE.EMPHASIS, fontWeight: 700, color: INK.PRIMARY }}>
				{t("request.outreach.approval.accepted_title", { name: signUp.name })}
				{moreWaiting > 0 && (
					<Box component="span" sx={{ fontWeight: 500, color: INK.SECONDARY }}>
						{` · ${t("request.outreach.approval.more_waiting", { count: moreWaiting })}`}
					</Box>
				)}
			</Typography>
			{full ? (
				<Typography data-testid="outreach-approval-full" sx={{ fontSize: FONT_SIZE.SECONDARY, fontWeight: 600, color: `${tone}.dark` }}>
					{t("request.outreach.approval.full", { name: signUp.name })}
				</Typography>
			) : (
				<Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.75 }}>
					<Typography component="span" sx={{ fontSize: FONT_SIZE.SECONDARY, fontWeight: 700, color: `${tone}.dark` }}>
						{t(recommended ? "request.outreach.approval.recommend_lead" : "request.outreach.approval.check_lead")}
					</Typography>
					{checksOf(t, signUp).map((check) => (
						<MetaChip
							key={check.id}
							size="small"
							tone={check.warn ? "warning" : "neutral"}
							label={check.label}
							dataTestId={`outreach-approval-check-${check.id}`}
						/>
					))}
				</Box>
			)}
		</Box>
	);
};
